import { useCallback, useEffect, useState } from "react";

import { api, ApiError } from "../api";
import type { NodeComment } from "../types";
import { Markdown } from "./Markdown";

interface Props {
  nodeId: string;
  initialCount: number;
  onCountChange: (count: number) => void;
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function NodeComments({ nodeId, initialCount, onCountChange }: Props) {
  const [comments, setComments] = useState<NodeComment[] | null>(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await api.listNodeComments(nodeId);
      setComments(list);
      onCountChange(list.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [nodeId, onCountChange]);

  useEffect(() => {
    setComments(null);
    setDraft("");
    setError(null);
    load();
  }, [nodeId, load]);

  const post = async () => {
    const body = draft.trim();
    if (body.length === 0) return;
    setPosting(true);
    setError(null);
    try {
      const created = await api.createNodeComment(nodeId, body);
      setComments((prev) => [...(prev ?? []), created]);
      setDraft("");
      onCountChange((comments?.length ?? 0) + 1);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError("Rate limited — try again in a minute.");
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setPosting(false);
    }
  };

  const remove = async (cid: number) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await api.deleteNodeComment(nodeId, cid);
      setComments((prev) => (prev ?? []).filter((c) => c.id !== cid));
      onCountChange(Math.max(0, (comments?.length ?? 1) - 1));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: "uppercase", color: "#475569" }}>
          Comments
        </h3>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>
          {comments === null ? "…" : `${comments.length}${initialCount !== comments.length ? ` (was ${initialCount})` : ""}`}
        </span>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: "#b91c1c", background: "#fee2e2", padding: "4px 8px", borderRadius: 4 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
        {comments?.map((c) => (
          <div
            key={c.id}
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 4, padding: "6px 8px" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 4 }}>
              <span>
                <strong style={{ color: c.author_label === "admin" ? "#2563eb" : "#334155" }}>
                  {c.author_label}
                </strong>
                {" · "}
                <span title={c.created_at}>{relTime(c.created_at)}</span>
              </span>
              <button
                onClick={() => remove(c.id)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  fontSize: 11,
                  padding: 0,
                }}
                title="Delete (soft)"
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 13 }}>
              <Markdown source={c.body} />
            </div>
          </div>
        ))}
        {comments !== null && comments.length === 0 && (
          <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
            No comments yet. Add one to start a timeline thread.
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              post();
            }
          }}
          placeholder="Append a timestamped note — supports markdown links [label](url)"
          rows={3}
          maxLength={4000}
          style={{
            padding: "6px 8px",
            border: "1px solid #cbd5e1",
            borderRadius: 4,
            fontSize: 13,
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>{draft.length} / 4000 · ⌘+Enter to post</span>
          <button
            disabled={posting || draft.trim().length === 0}
            onClick={post}
            style={{
              padding: "4px 12px",
              background: posting || draft.trim().length === 0 ? "#cbd5e1" : "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: posting || draft.trim().length === 0 ? "not-allowed" : "pointer",
              fontSize: 12,
            }}
          >
            {posting ? "Posting…" : "Post comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
